import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { InventoryItem } from "@dentacore/shared-types";

import { listInventory } from "../api/inventory";
import { useActiveBranch } from "../api/use-active-branch";
import { useAuth } from "../auth/auth-context";
import { useCan } from "../auth/use-can";
import { OrdersTab } from "../inventory/orders-tab";
import { SuppliersTab } from "../inventory/suppliers-tab";
import styles from "../inventory/inventory.module.css";
import { CartTab } from "./cart-tab";
import { useCart } from "./cart";
import { CatalogueTab } from "./catalogue-tab";
import { Toast } from "../settings/toast";
import { useWriter } from "../settings/use-writer";

/**
 * Which of Supply's pages this is (MS-D4, MS-D8): **Products → Catalogue and
 * Supplier items, Orders → Cart and Order history, People → Suppliers.** Each is
 * a link in the sidebar now, so the page carries no tab strip of its own.
 */
export type SupplySection = "catalogue" | "items" | "cart" | "history" | "suppliers";

const TITLES: Record<SupplySection, string> = {
  catalogue: "Catalog",
  items: "Supplier items",
  cart: "Cart",
  history: "Order history",
  suppliers: "Suppliers",
};

/**
 * **Supply — suppliers and their orders** (STORY-189a, MOD-D21).
 *
 * Moved out of Inventory, which is back to stock and procedure trays, so a
 * clinic without this add-on has a plain Inventory and a Supply item in the
 * sidebar that says what it is. The tabs themselves are the ones that were in
 * Inventory (STORY-171), unchanged — **only where they live moved**.
 *
 * The Catalogue joined them in STORY-189b and the Cart in STORY-189c.
 */
/**
 * The cart's provider is the shell's now (MS-D4), so the sidebar's Cart link can
 * count it and every Supply page shares the one cart.
 */
export function SupplyPage({ section }: { section: SupplySection }): React.JSX.Element {
  return <Supply section={section} />;
}

function Supply({ section }: { section: SupplySection }): React.JSX.Element {
  const navigate = useNavigate();
  const cart = useCart();
  const branchId = useActiveBranch();
  const { user } = useAuth();
  const can = useCan();
  const currency = user?.currency ?? "USD";
  const isAdmin = can("inventory.items.manage");
  const canOrder = can("inventory.movement.record");
  const { busy, toast, dismiss, fail, run } = useWriter();

  const [items, setItems] = useState<readonly InventoryItem[] | null>(null);

  const reloadItems = useCallback(async (signal?: AbortSignal) => {
    setItems(await listInventory(true, signal));
  }, []);

  useEffect(() => {
    if (branchId === null) return;
    const controller = new AbortController();
    setItems(null);
    reloadItems(controller.signal).catch((cause: unknown) => {
      if (controller.signal.aborted) return;
      setItems([]);
      fail(cause instanceof Error ? cause.message : "Stock could not be loaded.");
    });
    return () => controller.abort();
  }, [branchId, reloadItems, fail]);

  const active = (items ?? []).filter((item) => item.isActive);

  return (
    <div className={styles.page} data-testid="supply-page">
      <header className={styles.head}>
        <h1 className={styles.title}>
          {TITLES[section]}
          {section === "cart" && cart.lines.length > 0 && ` (${String(cart.lines.length)})`}
        </h1>
      </header>

      {section === "catalogue" ? (
        <CatalogueTab
          currency={currency}
          canManage={isAdmin}
          canOrder={canOrder}
          busy={busy}
          run={run}
          resetOn={branchId}
        />
      ) : section === "cart" ? (
        <CartTab
          currency={currency}
          canOrder={canOrder}
          busy={busy}
          run={run}
          onPlaced={() => {
            navigate("/orders/history");
          }}
        />
      ) : section === "suppliers" || section === "items" ? (
        <SuppliersTab
          // People → Suppliers is the contact card and its product list;
          // Products → Supplier items is the same lines, product side (MS-D8).
          view={section === "items" ? "items" : "people"}
          items={active}
          currency={currency}
          canManage={isAdmin}
          busy={busy}
          run={run}
          fail={fail}
          onItemsChanged={() => reloadItems()}
          resetOn={branchId}
        />
      ) : (
        <OrdersTab
          items={active}
          currency={currency}
          canOrder={canOrder}
          canManagePrices={isAdmin}
          busy={busy}
          run={run}
          fail={fail}
          onStockChanged={() => reloadItems()}
          resetOn={branchId}
        />
      )}

      {toast !== null && <Toast kind={toast.kind} message={toast.message} onDismiss={dismiss} />}
    </div>
  );
}
